import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useBranding } from '../../branding/BrandingContext';
import { useAuthSession } from '../../contexts/AuthSessionContext';

const buildRedirectState = ({
  ownerOnly,
  allowCheflistAdmin,
  allowAtendabotManager,
  skipBilling,
  skipRestaurantCheck,
  product,
  allowTrialRestricted,
  allowAtendabotAtendente,
  foodOnly,
  isOwner,
  cheflistAdmin,
  atendabotAtendente,
  hasBillingAccess,
  hasRestaurantAccess,
  hasProductAccess,
  isFoodUser,
  defaultRedirectPath,
  productAccessDeniedPath,
  scopedProductKey,
  scopedProductEntryPath,
  isAtendabotBrand,
}) => {
  if (ownerOnly) {
    const hasAtendabotManagerAccess =
      allowAtendabotManager
      && atendabotAtendente !== true
      && typeof hasProductAccess === 'function'
      && hasProductAccess('atendabot');
    const hasOwnerAccess =
      isOwner
      || (allowCheflistAdmin && cheflistAdmin)
      || hasAtendabotManagerAccess;

    if (!hasOwnerAccess) {
      return {
        path: defaultRedirectPath,
        message: 'Você não tem permissão para acessar essa página.',
        toastId: 'no-owner-access',
      };
    }
  }

  if (!skipBilling && !hasBillingAccess) {
    return {
      path: '/billing',
      message: isAtendabotBrand
        ? 'Acesso pausado. Ative sua assinatura do AtendaBot para continuar.'
        : 'Acesso pausado. Ative os produtos para continuar quando quiser.',
      toastId: 'billing-required',
    };
  }

  if (!skipRestaurantCheck && !hasRestaurantAccess) {
    return {
      path: '/restaurante-inativo',
      message: isAtendabotBrand
        ? 'Conta inativa. Entre em contato com o administrador.'
        : 'Restaurante inativo. Entre em contato com o administrador.',
      toastId: 'restaurant-inactive',
    };
  }

  const hasAccessToProduct = product
    ? hasProductAccess(product, { allowTrialRestricted })
    : true;

  if (product && !hasAccessToProduct) {
    const hasScopedProductEntryAccess =
      product === scopedProductKey
      && hasProductAccess(product, { allowTrialRestricted: true });

    return {
      path:
        product === scopedProductKey
          ? (hasScopedProductEntryAccess
            ? scopedProductEntryPath
            : productAccessDeniedPath)
          : defaultRedirectPath,
      message: hasScopedProductEntryAccess
        ? 'Esta página não está disponível no seu acesso atual.'
        : 'Você não tem acesso a esse produto.',
      toastId: hasScopedProductEntryAccess
        ? `restricted-access-${product}`
        : `no-access-${product}`,
    };
  }

  if (product === 'atendabot' && foodOnly !== null) {
    const hasFoodAccess = isFoodUser === true;

    if ((foodOnly && !hasFoodAccess) || (!foodOnly && hasFoodAccess)) {
      return {
        path: '/atendabot',
        message: 'Você não tem acesso a essa página.',
        toastId: `no-access-${product}-page`,
      };
    }
  }

  if (product === 'atendabot' && atendabotAtendente && !allowAtendabotAtendente) {
    return {
      path: '/atendabot',
      message: 'Você não tem acesso a essa página.',
      toastId: 'no-access-atendabot-atendente-page',
    };
  }

  return null;
};

const PrivateRoute = ({
  children,
  adminOnly = false,
  ownerOnly = false,
  product = null,
  foodOnly = null,
  allowCheflistAdmin = false,
  allowAtendabotManager = false,
  skipBilling = false,
  skipRestaurantCheck = false,
  allowTrialRestricted = false,
  allowAtendabotAtendente = false,
}) => {
  const navigate = useNavigate();
  const branding = useBranding();
  const {
    session,
    isAuthenticated,
    isHydrating,
    sessionError,
    retrySession,
    isAdmin,
    isOwner,
    cheflistAdmin,
    atendabotAtendente,
    isFoodUser,
    hasBillingAccess,
    hasRestaurantAccess,
    hasProductAccess,
    getProductEntryPath,
  } = useAuthSession();
  const defaultRedirectPath = branding?.authenticatedPath || '/products';
  const productAccessDeniedPath = branding?.accessDeniedPath || defaultRedirectPath;
  const effectiveIsFoodUser =
    typeof branding?.isFood === 'boolean' ? branding.isFood : isFoodUser;
  const scopedProductEntryPath =
    branding?.productKey && typeof getProductEntryPath === 'function'
      ? getProductEntryPath(branding.productKey, defaultRedirectPath)
      : defaultRedirectPath;

  const redirectState =
    isAuthenticated && !isHydrating && session
      ? buildRedirectState({
        ownerOnly,
        allowCheflistAdmin,
        allowAtendabotManager,
        skipBilling,
        skipRestaurantCheck,
        product,
        allowTrialRestricted,
        allowAtendabotAtendente,
        foodOnly,
        isOwner,
        cheflistAdmin,
        atendabotAtendente,
        hasBillingAccess,
        hasRestaurantAccess,
        hasProductAccess,
        isFoodUser: effectiveIsFoodUser,
        defaultRedirectPath,
        productAccessDeniedPath,
        scopedProductKey: branding?.productKey || null,
        scopedProductEntryPath,
        isAtendabotBrand: branding?.key === 'atendabot',
      })
      : null;

  useEffect(() => {
    if (!redirectState?.path || !redirectState?.message || !redirectState?.toastId) {
      return undefined;
    }

    toast.error(redirectState.message, { toastId: redirectState.toastId });
    const timeoutId = window.setTimeout(() => {
      navigate(redirectState.path, { replace: true });
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [navigate, redirectState]);

  if (isHydrating) return <div role="status">Carregando sessão...</div>;
  if (sessionError) return <div role="alert">Não foi possível verificar sua sessão. <button onClick={() => { void retrySession().catch(() => {}); }}>Tentar novamente</button></div>;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (!session) return <Navigate to="/" replace />;

  if (adminOnly && !isAdmin) {
    return <Navigate to={defaultRedirectPath} replace />;
  }

  if (redirectState) {
    return null;
  }

  return children;
};

export default PrivateRoute;
